import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { File } from '../types';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const STORAGE_KEY = (id: string) => `nt-file:video:${id}:position`;
const RESUME_THRESHOLD = 10;

interface UseVideoPlayerOptions {
  hideControlsAfterMs?: number;
}

export type PlaybackRate = 0.5 | 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2;
export const PLAYBACK_RATES: readonly PlaybackRate[] = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export function useVideoPlayer(file: File, options: UseVideoPlayerOptions = {}) {
  const hideAfter = options.hideControlsAfterMs ?? 3000;

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const savePositionRef = useRef<ReturnType<typeof setTimeout>>();

  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPip, setIsPip] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(file.duration || 0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState<PlaybackRate>(1);
  const [showControls, setShowControls] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [showTimeAsRemaining, setShowTimeAsRemaining] = useState(false);
  const [savedPosition, setSavedPosition] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY(file.id));
      if (!stored) return null;
      const parsed = Number(stored);
      return Number.isFinite(parsed) && parsed > RESUME_THRESHOLD ? parsed : null;
    } catch {
      return null;
    }
  });

  const streamProxyUrl = useMemo(
    () => `${API_BASE_URL}/files/${file.id}/stream-proxy`,
    [file.id],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (savePositionRef.current) clearTimeout(savePositionRef.current);
      savePositionRef.current = setTimeout(() => {
        try {
          if (video.currentTime > RESUME_THRESHOLD && video.currentTime < video.duration - 5) {
            localStorage.setItem(STORAGE_KEY(file.id), String(video.currentTime));
          }
        } catch {
          /* ignore quota errors */
        }
      }, 1500);
    };

    const handleLoadedMetadata = () => {
      if (video.duration && !isNaN(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setAspectRatio(video.videoWidth / video.videoHeight);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      try {
        localStorage.removeItem(STORAGE_KEY(file.id));
      } catch {
        /* ignore */
      }
      setSavedPosition(null);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);
    const handleCanPlay = () => setIsBuffering(false);
    const handleEnterPip = () => setIsPip(true);
    const handleLeavePip = () => setIsPip(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('enterpictureinpicture', handleEnterPip);
    video.addEventListener('leavepictureinpicture', handleLeavePip);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('enterpictureinpicture', handleEnterPip);
      video.removeEventListener('leavepictureinpicture', handleLeavePip);
      if (savePositionRef.current) clearTimeout(savePositionRef.current);
    };
  }, [file.id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, []);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, hideAfter);
  }, [isPlaying, hideAfter]);

  const handleMouseLeave = useCallback(() => {
    if (isPlaying) setShowControls(false);
  }, [isPlaying]);

  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setHasStarted(true);
    void video.play();
  }, []);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setHasStarted(true);
    if (video.paused) void video.play();
    else video.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    video.muted = next;
    setIsMuted(next);
  }, []);

  const setVolumeAt = useCallback((value: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = value;
    setVolume(value);
    const muted = value === 0;
    video.muted = muted;
    setIsMuted(muted);
  }, []);

  const seekTo = useCallback((time: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = time;
    setCurrentTime(time);
  }, []);

  const skip = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.currentTime + seconds, duration));
  }, [duration]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void containerRef.current?.requestFullscreen();
    }
  }, []);

  const togglePip = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !document.pictureInPictureEnabled) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      /* user denied / not supported */
    }
  }, []);

  const setRate = useCallback((rate: PlaybackRate) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
  }, []);

  const toggleTimeDisplay = useCallback(() => {
    setShowTimeAsRemaining((v) => !v);
  }, []);

  const resumeFromSaved = useCallback(() => {
    if (savedPosition == null) return;
    const video = videoRef.current;
    if (!video) return;
    setHasStarted(true);
    video.currentTime = savedPosition;
    void video.play();
    setSavedPosition(null);
  }, [savedPosition]);

  const startOver = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setHasStarted(true);
    video.currentTime = 0;
    void video.play();
    setSavedPosition(null);
    try {
      localStorage.removeItem(STORAGE_KEY(file.id));
    } catch {
      /* ignore */
    }
  }, [file.id]);

  return {
    videoRef,
    containerRef,
    streamProxyUrl,
    state: {
      hasStarted,
      isPlaying,
      isMuted,
      isFullscreen,
      isPip,
      isBuffering,
      currentTime,
      duration,
      volume,
      playbackRate,
      showControls,
      aspectRatio,
      showTimeAsRemaining,
      savedPosition,
    },
    actions: {
      play,
      pause,
      togglePlay,
      toggleMute,
      setVolumeAt,
      seekTo,
      skip,
      toggleFullscreen,
      togglePip,
      setRate,
      toggleTimeDisplay,
      resumeFromSaved,
      startOver,
      handleMouseMove,
      handleMouseLeave,
    },
  };
}
